import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import { User, Lock, Check, Eye, EyeOff } from 'lucide-react';

export default function Profile() {
  const { user, setUser } = useAuth();

  const [username, setUsername] = useState(user?.username || '');
  const [savingUsername, setSavingUsername] = useState(false);

  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const [savingPw, setSavingPw] = useState(false);

  const handleUpdateUsername = async (e) => {
    e.preventDefault();
    if (username === user?.username) return;
    if (username.trim().length < 3) { toast.error('Username must be at least 3 characters'); return; }
    setSavingUsername(true);
    try {
      const r = await authAPI.updateUsername(username.trim());
      setUser(r.data);
      toast.success('Username updated ✓');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update username');
    } finally {
      setSavingUsername(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwords.current) { toast.error('Enter your current password'); return; }
    if (passwords.next.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (passwords.next !== passwords.confirm) { toast.error('Passwords do not match'); return; }
    setSavingPw(true);
    try {
      await authAPI.changePassword(passwords.current, passwords.next);
      toast.success('Password changed ✓');
      setPasswords({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPw(false);
    }
  };

  const pwInput = (field, placeholder) => (
    <div style={{ position: 'relative' }}>
      <input
        type={showPw[field] ? 'text' : 'password'}
        value={passwords[field]}
        onChange={e => setPasswords(p => ({ ...p, [field]: e.target.value }))}
        placeholder={placeholder}
        style={{ paddingRight: '2.5rem' }}
      />
      <button type="button"
        onClick={() => setShowPw(s => ({ ...s, [field]: !s[field] }))}
        style={{ position: 'absolute', right: '0.7rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', padding: 0 }}>
        {showPw[field] ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );

  return (
    <Layout>
      <div className="page-header">
        <h1>Profile</h1>
        <p>Manage your account settings</p>
      </div>

      <div style={{ maxWidth: 480, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Account info */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.25rem' }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-soft)' }}>
              <User size={16} />
            </div>
            <span style={{ fontWeight: 700, fontSize: '0.92rem' }}>Account Info</span>
          </div>

          <div style={{ marginBottom: '1rem', padding: '0.7rem 0.9rem', background: 'var(--bg-deep)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-faint)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Email</div>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>{user?.email}</div>
          </div>

          <div style={{ marginBottom: '1rem', padding: '0.7rem 0.9rem', background: 'var(--bg-deep)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-faint)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Day Streak</div>
            <div style={{ fontSize: '0.9rem', color: 'var(--orange)', fontWeight: 700 }}>🔥 {user?.streak_count || 0} days</div>
          </div>

          <form onSubmit={handleUpdateUsername}>
            <div className="form-group">
              <label>Username</label>
              <input
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="Your username"
              />
            </div>
            <button className="btn btn-primary" type="submit"
              disabled={savingUsername || username === user?.username || username.trim().length < 3}
              style={{ width: 'auto', paddingInline: '1.4rem' }}>
              {savingUsername ? 'Saving…' : <><Check size={13} /> Save Username</>}
            </button>
          </form>
        </div>

        {/* Change password */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.25rem' }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-soft)' }}>
              <Lock size={16} />
            </div>
            <span style={{ fontWeight: 700, fontSize: '0.92rem' }}>Change Password</span>
          </div>

          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label>Current Password</label>
              {pwInput('current', 'Enter current password')}
            </div>
            <div className="form-group">
              <label>New Password</label>
              {pwInput('next', 'At least 6 characters')}
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              {pwInput('confirm', 'Repeat new password')}
            </div>

            {/* Password strength indicator */}
            {passwords.next && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '0.3rem' }}>
                  {[1,2,3,4].map(i => {
                    const strength = passwords.next.length >= 12 ? 4 : passwords.next.length >= 8 ? 3 : passwords.next.length >= 6 ? 2 : 1;
                    const colors = { 1: 'var(--red)', 2: 'var(--orange)', 3: 'var(--yellow)', 4: 'var(--green)' };
                    return (
                      <div key={i} style={{ flex: 1, height: 3, borderRadius: 99, background: i <= strength ? colors[strength] : 'var(--bg-raised)', transition: 'all 0.2s' }} />
                    );
                  })}
                </div>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-faint)' }}>
                  {passwords.next.length < 6 ? 'Too short' : passwords.next.length < 8 ? 'Weak' : passwords.next.length < 12 ? 'Good' : 'Strong'}
                </span>
              </div>
            )}

            <button className="btn btn-primary" type="submit" disabled={savingPw}
              style={{ width: 'auto', paddingInline: '1.4rem' }}>
              {savingPw ? 'Saving…' : <><Lock size={13} /> Change Password</>}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
